/**
 * View Source Documents modal
 * Created on 27-05-24
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifFormField,
  MotifCarousel,
} from "@ey-xd/motif-react";
import { MotifIconButton, MotifFab } from "@ey-xd/motif-react/components/button";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import _ from "lodash";
import { MotifInput, MotifOption, MotifSelect, MotifLabel, MotifTooltip, MotifTruncate,MotifBadge} from "@ey-xd/motif-react";
//  Custom imports
import "./ViewSourceDocsModal.css";
import DownloadDocument from "../../utils/downloadDocument";
import iconNavArrowLeft from "../../assets/images/byod-images/nav_arrow_left.svg";
import iconNavArrowRight from "../../assets/images/byod-images/nav_arrow_right.svg";
import iconDownload from "../../assets/images/byod-images/download.svg";
import DocumentService from "../../services/document.service";
import CommonService from "../../services/common.service";
import Utils from "../../utils/Utils";

import XLViewer from "./XLViewer";
import MarkdownWithoutStream from "./MarkdownWithoutStream.js";

import { tooltip } from '../../commonImage';
import loadingCircle from "../../assets/images/byod-images/Loading_indicator_circlesTable.svg";
import { useSelector } from "react-redux";
import { formatFileName } from '../../utils/commonUtils'
//  React pdf initialization
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const ViewSourceDocsModal = (props) => {
  const [varReferences, setVarReferences] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [pages, setPages] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState(``);
  const [document, setDocument] = useState(null);
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [isDocLoaded, setIsDocLoaded] = useState(false);
  const [pageWidth, setPageWidth] = useState(0);
  const [modalBodyHeight, setModalBodyHeight] = useState('100%');
  const [modalSrcRefHeight, setModalSrcRefHeight] = useState('100%');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedPages, setSelectedPages] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedFileRefStartIndex, setSelectedFileRefStartIndex] = useState(0);
  const containerRef = useRef(null);
  const containerBodyRef = useRef(null);
  const responseRef = useRef(null);

  const downloadDocument = new DownloadDocument();
  const validFileExtensions = [".docx", ".doc", ".ppt", ".pptx", ".pdf", "xlsx"];
  const validExcelFileExtensions = ["xlsx"];
  const documentservice = new DocumentService();
  const commonService = new CommonService();
  const utils = new Utils();
  const msgLoading = "Loading file please wait...";
  const [msgNoData, setMsgNoData] = useState(
    "File type not supported in preview"
  );
  const msgNoDocs = "No source documents.";
  const xlViewerRef = useRef();

  const documentThreads = useSelector((state => state.userChat.selectedDocumentThread));


  /**
   * PDF Viewer related functions
   * @param {*} offset
   * @returns
   */
  const changePage = (offset) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
    setCurrentPageNumber((prevPageNumber) => prevPageNumber + offset);
  }
  
  const previousPage = () => changePage(-1);
  const nextPage = () => changePage(1);
  const handleOnLoadError = (err) => {
    commonService.logException(
      err,
      "View Source Documents Component",
      "Handle Document onLoadError"
    );
    setIsDocLoaded(true);
  };

  /**
   * This method will call when document loads
   * @param {number} numPages will provide the toatal number of pages available 
   */
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    updatePageWidth();
    setPageNumber(1);
    setCurrentPageNumber(1);
    let vPages = [];
    for (let index = 1; index <= numPages; index++) {
      vPages.push({ value: index, label: index });
    }
    setPages(vPages);
    setIsDocLoaded(true);
  };

  /**
   * This method will provides the dynamic width to the document and refrence based on that parent div
   */
  const updatePageWidth = () => {
    if (containerBodyRef.current) {
      const containerHeight = containerBodyRef.current.offsetHeight;
      setModalBodyHeight(containerHeight - 100 + 'px');
      setModalSrcRefHeight(containerHeight - 65 + 'px')
    }
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      setPageWidth(containerWidth - 10);
    }
  };

  /**
   * Highlight Pattern in PDF
   * @param {*} text
   * @param {*} pattern
   * @returns
   */
  const highlightPattern = (text, pattern) => {
    if (
      text.trim() &&
      pattern.toLowerCase().includes(text.toLowerCase()) &&
      text.split(" ").length > 5
    ) {
      return `<mark>${text}</mark>`;
    }
  };

  /**
   * Custom Text renderer for PDF to highlight text
   */
  const textRenderer = useCallback(
    (textItem) => {
      const vPageNumbers = _.filter(varReferences, function (item) {
        return item.fileName === fileName;
      });
      if (
        vPageNumbers.length > 0 &&
        vPageNumbers[0].pages.length > 0 &&
        vPageNumbers[0].pages.includes(textItem.pageNumber)
      ) {
        return highlightPattern(textItem.str, searchText);
      }
    },
    //  eslint-disable-next-line
    [searchText, fileName]
  );

  /**
   * To handle page change from the page input text
   * @param {*} e
   */
  const goToPageNumber = (e) => {
    if (e.target.value !== "" && e.target.value !== "0") {
      if (parseInt(e.target.value) >= 1 && parseInt(e.target.value) <= pages.length) {
        setPageNumber(parseInt(e.target.value));
        setCurrentPageNumber(parseInt(e.target.value));
      }
    } 
  }
  /**
   * Hook to format references and chunks initially
   */
  useEffect(() => {
    if (props.references) {
      formatReferences(props.references);
      formatChunks(props.chunks);
      getDeletedDcoumnets();      
    }
    //  eslint-disable-next-line
  }, [props.references, props.chunks]);

  /**
   * Hook to set initial file name
   */
  useEffect(() => {
    getInitFileName(varReferences);
    //  eslint-disable-next-line
  }, [varReferences]);

  /**
   * To get the document url for the file name to pass to PDF viewer
   */
  useEffect(() => {
    if (fileName) {
      getDocumentUrl(fileName);
    }
    //  eslint-disable-next-line
  }, [fileName]);

  /**
   * This useEffect is used to show/hide the modal
   */
  useEffect(() => {
    props.setOpenModal(props.openShow);
    //  eslint-disable-next-line
  }, [props.setOpenModal]);

  /**
   * To format references
   * @param {*} vResponse
   */
  const formatReferences = (vResponse) => {
    const vFiles = vResponse;
    let vReferences = [];
    if (vFiles) {
      let vRefStartIndex = 0;
      if (typeof vFiles === "string") {
        const filesArr = vFiles.split(",");
        for (const file of filesArr) {
          if (file) {
            const pageNoArr = file.split(":");
            let vFileName;
            let vPages = [];
            for (let i = 0; i < pageNoArr.length; i++) {
              //vPageRef are refernce numbers for both PDF and EXCEl
              let vPageRef 
              if(isFileExcel(file)){
                vPageRef =  pageNoArr[i];
              }
              else{
                vPageRef = parseInt(pageNoArr[i])
              }
              if (i === 0) {
                vFileName = pageNoArr[i];
              } else {
                vPages.push(vPageRef);
              }
            }
            let vIsBroken= false;
            if(props?.source === "EYD" && props.sourceDocuments?.length>0){
              vIsBroken = _.some(props.sourceDocuments, function(doc) {
                return doc.documentName === vFileName && doc.isAvailable=== false && doc.isBroken==="Y"
              });
            }
            vReferences.push({
              fileName: formatFileName(vFileName),
              pages: vPages,
              isBroken: vIsBroken,
              refStartIndex: vRefStartIndex + 1
            });
            vRefStartIndex += pageNoArr.length - 1;
          }
        }
      } else {
        for (const element of vFiles) {
          let vIsBroken = false;
          if (element.isBroken) {
            if (typeof element.isBroken === "string") {
              if (element.isBroken === "true") {
                vIsBroken = true;
              } else {
                vIsBroken = false;
              }
            } else if (element.isBroken) {
              vIsBroken = true;
            } else {
              vIsBroken = false;
            }
          }

          const vFileName = element.fileName || element.FileName;
          const formattedFileName = formatFileName(vFileName);
          const vPageNos = element.pageNumber || element.PageNumber;
          const vPages = isFileExcel(vFileName) ? element.sheetName : vPageNos;
          vReferences.push({
            fileName: formattedFileName,
            pages: vPages,
            isBroken: vIsBroken,
            refStartIndex: vRefStartIndex + 1
          });
          vRefStartIndex += vPageNos.length - 1;
        }
      }
    }

    setVarReferences(vReferences);
    setSelectedPages(vReferences[0].pages)
    setSelectedFileRefStartIndex(vReferences[0].refStartIndex);
  };
  /**
   * To format chunks
   * @param {*} vChunks
   */
  const formatChunks = (vChunks) => {
    let strChunks = "";
    if (typeof vChunks === "string" && vChunks !== "") {
      try {
        vChunks = JSON.parse(vChunks);
      } catch (e) {}
    }
    if (vChunks) {
      for (const element of vChunks) {
        strChunks += " " + element;
      }
    }
    setSearchText(strChunks);
  };

  /**
   * Function to set initial file name
   * @param {*} vRefs
   * @returns
   */
  const getInitFileName = (vRefs) => {
    if (vRefs) {
      for (const vRef of vRefs) {
        //ask to yogesha
        if(props?.source === "EYD" && props.sourceDocuments?.length>0){
          const isDocumentDeleted = _.some(props.sourceDocuments, function(doc) {return doc.documentName === vRef.fileName && doc.isAvailable=== false && doc.isBroken==="Y" });
          if(isDocumentDeleted){
            setMsgNoData("Document is deleted");
            setIsDocLoaded(true);
            return;
          }
        }
        if(props?.source === "Favorite") {
          if(props?.isBroken === "Y"){
            setMsgNoData("Document is deleted");
            setIsDocLoaded(true);
            return;
          }
        }

        if(props?.source === "II") {
          if(vRef.isBroken){
            setMsgNoData("Document is deleted");
            setIsDocLoaded(true);
            return;
          }
          //
        }
        for (const vExt of validFileExtensions) {
          if (vRef.fileName.indexOf(vExt) !== -1) {
            setFileName(vRef.fileName);
            return;
          }
        }
      }
    }
    if (!fileName) {
      if (vRefs.length > 0) {
        setMsgNoData("File type not supported in preview");
        setIsDocLoaded(true);
      }
    }
  };

  /**
   * Function to get Document url
   * @param {*} vFile
   */
  const getDocumentUrl = (vFile) => {
    if (isFileExcel(vFile)) {
      downloadDocument.getFileExcel(vFile, documentThreads.threadId).then(async (res) => {
        if (res) {
          const data = await res.arrayBuffer();
          const base64Data = btoa(Array.from(new Uint8Array(data))
            .map(byte => String.fromCharCode(byte))
            .join(''));
          setDocument(base64Data);
        } else {
          setMsgNoData("Excel file is either deleted or unable to load.");
          setIsDocLoaded(true);
        }
      });
    } else {
      if (vFile && vFile.indexOf(".pdf") === -1) {
        vFile = vFile.substr(0, vFile.indexOf(".")) + ".pdf";
      }
      downloadDocument.getDownloadUrl(vFile, documentThreads.threadId).then((res) => {
        if (res) {
          setDocument(res);
        } else {
          setMsgNoData("PDF file is either deleted or unable to load.");
          setIsDocLoaded(true);
        }
      });
    }
  };

  /**
   * Function to navigate to page
   * @param {*} vPageNo
   * @param {*} vFileName
   * @param {*} vPageIndex
   */
  const navigateToPage = (vPageNo) => {
    setPageNumber(parseInt(vPageNo));
    setCurrentPageNumber(parseInt(vPageNo));
  };

  /**
   * Check if valid file name
   */
  const isValidFileName = (vFileName) => {
    if (vFileName) {
      for (const element of validFileExtensions) {
        if (vFileName.toLowerCase().indexOf(element) !== -1) {
          return true;
        }
      }
    }
    return false;
  };

   /**
   * Function to navigate to sheet
   * @param {*} vSheetName 
   * @param {*} vFileName 
   * @param {*} vPageIndex 
   */
   const navigateToSheet = (vSheetName) => {
    setTimeout(() => {
      if (xlViewerRef.current) {
        xlViewerRef.current.gotoSheet(vSheetName);
        handlePageClick(vSheetName);
      }
    }, 1000);
  };


  /**
   * Check if document deleted
   */
  const isDocumentDeleted = (vFileName) => {
    if (vFileName) {
      for (const element of deletedDocuments) {
        if (element === vFileName) {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * Get deleted documents
   */
  const getDeletedDcoumnets = async () => {
    const vDeletedDocNames = [];
    const vDocuments = await documentservice.getDocuments(
      documentThreads.threadId,
    );
    if (vDocuments.length > 0) {
      const vDeletedDocs = _.filter(vDocuments, function (item) {
        return item.status === "Deleted" && item.deleteTS>props.promptTs;
      });
      if (vDeletedDocs.length > 0) {
        for (const element of vDeletedDocs) {
          vDeletedDocNames.push(element.documentName);
        }
      }
    }
    setDeletedDocuments(vDeletedDocNames);
  };

   /**
   * Check if file is Excel
   */
   const isFileExcel = (vFileName) => {
    if (vFileName) {
      for (const element of validExcelFileExtensions) {
        if (vFileName.toLowerCase().indexOf(element) !== -1) {
          return true;
        }
      }
    }
    return false;
  };

  const handleDropdownChange = (vRef) => {
    if (fileName !== vRef.fileName) {
      setDocument(null);
      setIsDocLoaded(false);
      setFileName(vRef.fileName);
      getDocumentUrl(vRef.fileName);
      // const Reference ={
      //   pages:vRef.pages,
      //   order:vRef.refStartIndex
      // }
      setSelectedPages(vRef.pages);
      setSelectedFileRefStartIndex(vRef.refStartIndex);
    }
  };

  const chunkedPages = (vArray, vChunkSize) => {
    const vResult = [];
    if (vArray) {
      for(let i = 0; i < vArray.length; i += vChunkSize) {
        vResult.push(vArray.slice(i, i + vChunkSize));
      }
    }
    return vResult;
  };

  const handlePageClick = (vPage) => {
    setSelectedPage(vPage);
    navigateToPage(vPage);
  };
  return (
    <div>
      <MotifModal
        id="sourceDocumentModal"
        className="source-document-modal"
        show={props.setOpenModal}
        onClose={() => {
          props.setOpenModal(!props.openShow);
        }}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
        size="xl"
      >
        <MotifModalHeader className="modal-header-container">
          View Source Documents
        </MotifModalHeader>
        <MotifModalBody>
        <div className="view-source-document-body">
            <div className="explore-prompt-question-response-container-child promptcontainer">
              <div
                id="favResponse"
                ref={responseRef}
                className={
                  "explore-prompt-question-response prompt-container divResponse" +
                  props.oResponse?.promptId
                }
              >
                 <MarkdownWithoutStream markdownText={props.oResponse && props.oResponse.responseContent ? props.oResponse.responseContent : props.oResponse?.promptResponse ? props.oResponse?.promptResponse : props.oResponse.response}
                                  setMarkdownResponseHTMLString={() => { }} />
              </div>
            </div>
            <div className="source-document-body document-modal-container">

            <div className="source-document-reference-container">
              <div className="reference-container-layout">
                <div className="srcDocsWrapper cross-icon-hide">
                  <MotifFormField className="form-field">
                    <MotifLabel id="select-document-label" className="reference-header-container">
                      Select Document
                    </MotifLabel>
                    <MotifSelect
                      className="selectInput viewsourcedocument"
                      ariaLabelledBy="select-document-label"
                      searchPlaceholder="Search"
                      filter={true}
                      noResultsTemplate= {() =>{return "No documents found"}}
                      value={fileName}
                      visibleOptions={3}
                    >
                      {varReferences?.map((vRef, vRefIndex) => (
                        <MotifOption
                          key={vRefIndex}
                          value={vRef.fileName}
                          className="languageName"
                          onClick={() => handleDropdownChange(vRef)}
                          disabled={
                            isDocumentDeleted(vRef.fileName) || 
                            vRef.isBroken ||
                            !isValidFileName(vRef.fileName)
                          }
                        >
                          {vRef.fileName}
                        </MotifOption>
                      ))}
                    </MotifSelect>
                  </MotifFormField>
                  {/* <div className="source-wrapper">
                  {varReferences.map((vRef, vRefIndex) => (
                    <div className="srcDocsGroup" key={vRefIndex}>
                      {(isDocumentDeleted(vRef.fileName) || vRef.isBroken) && (
                        <div className="brokenLinkAccord">
                          {vRef.fileName}
                          <MotifTooltip
                            trigger={
                              <img
                                src={iconNoLink}
                                alt="Document deleted"
                                className="iconNoLink"
                              ></img>
                            }
                          >
                            Document deleted
                          </MotifTooltip>
                        </div>
                      )}
                      {!(isDocumentDeleted(vRef.fileName) || vRef.isBroken) &&
                        isValidFileName(vRef.fileName) && (
                          <MotifAccordion
                            id={`refAccordion${vRefIndex}`}
                            onClose={() => setSelectedAccordion(null)}
                            onOpen={() => setSelectedAccordion(vRefIndex)}
                            style={{
                              flex: "1",
                            }}
                            alignIconRight={true}
                            useChevronIcon={true}
                            className={
                              selectedAccordion === vRefIndex
                                ? "blue-highlight"
                                : ""
                            }
                          >
                            <MotifAccordionTrigger>
                              <div className="accordionTitle">
                                {vRef.fileName}
                              </div>
                              <MotifIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadDocument.handleDownload(
                                    vRef.fileName
                                  );
                                }}
                                className="btnDownload"
                              >
                                <img
                                  src={download}
                                  alt="download source documents"
                                ></img>
                              </MotifIconButton>
                            </MotifAccordionTrigger>
                            <MotifAccordionContent className="srcDocsContent">
                              {vRef.pages.map((vPage, vPageIndex) => (
                                <div key={vRefIndex + vPageIndex}>
                                  <MotifIconButton
                                    aria-label="Reference"
                                    onClick={() =>
                                      navigateToPage(
                                        vPage,
                                        vRef.fileName,
                                        vRefIndex + "~" + vPageIndex
                                      )
                                    }
                                    className="referenceLink"
                                  >
                                    Reference {vPageIndex + 1}: page {vPage}
                                    {selectedRef ===
                                      vRefIndex + "~" + vPageIndex && (
                                      <img src={iconTick} className="iconTick" alt="Selected" />
                                    )}
                                  </MotifIconButton>
                                </div>
                              ))}
                            </MotifAccordionContent>
                          </MotifAccordion>
                        )}
                      {!(isDocumentDeleted(vRef.fileName) || vRef.isBroken) &&
                        !isValidFileName(vRef.fileName) && (
                          <div className="disabledAccord">{vRef.fileName}</div>
                        )}
                    </div>
                  ))}
                  </div> */}
                </div>
              </div>
            </div>
            <div className="source-document-container" ref={containerBodyRef}>
                <div className="source-document-title">
                  <MotifTruncate
                    maxLines={1}
                    tooltipProps={{placement: "bottom", hideCloseButton: true}}
                    className="motiftruncatemaxwidth2"
                  >                    
                  {fileName}
                  </MotifTruncate>
                
                  <div className="paginatorfooter">
                  <MotifTooltip
                      placement="bottom"
                      allowClick={false}
                      contentClassName="tooltipStyle"
                      trigger={
                      <div className="Left-btn-grp">
                      <MotifIconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadDocument.handleDownload(
                            fileName,
                            documentThreads.threadId
                          );
                        }}
                        className="btnDownload btnstyle"
                      >
                        <img
                          src={iconDownload}
                          alt="download source documents"
                        ></img>
                      </MotifIconButton>
                      </div>
                      }
                    >
                      Download
                    </MotifTooltip>
                   {
                !isFileExcel(fileName) &&
                  <div className="paginatorWrapper">
                  <MotifIconButton
                    aria-label="Previous Page"
                    disabled={pageNumber <= 1}
                    onClick={previousPage}
                    className="iconPrevious"
                  >
                    <img src={iconNavArrowLeft} alt="Arrow Left" />
                  </MotifIconButton>
                  <div className="pageSelect">
                    {(pages.length > 0) &&
                      <MotifInput
                                    label="inputprojectname"
                                    onChange={(e)=>setCurrentPageNumber(e.target.value)}
                                    data-testid="inputprojectname"
                                    className='pageInput'
                                    value={currentPageNumber}
                                    hideClearButton={true}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                          goToPageNumber(e)
                                      }}
                                      width={30}
                                />
                    }
                  </div>
                  <div className="txtPageNo">/{numPages || "--"}</div>
                  <MotifIconButton
                    aria-label="Next Page"
                    disabled={pageNumber >= numPages}
                    onClick={nextPage}
                    className="iconNext"
                  >
                    <img src={iconNavArrowRight} alt="Arrow Left" />
                  </MotifIconButton>
                </div>
               }
               </div>
               </div>
              <div className="pageRefContainer">
                  <div className="pageRefCont">
                    <MotifTooltip
                      placement="right"
                      allowClick={false}
                      contentClassName="tooltipStyle"
                      trigger={
                        <img className="info-icon-byod" src={tooltip} alt="" />
                      }
                    >
                      Page reference reflects the position in the document viewer<br />rather than the number shown on the pages in the document
                    </MotifTooltip>
                    <span className="reference-text">
                      References:
                    </span>
                  </div>

                {/* {selectedPages?.map((vPage, vPageIndex) => (
                  <MotifFab
                    aria-label="Reference"
                    onClick={() =>
                      navigateToPage(vPage)
                    }
                    className="referenceLink"
                    key={vPageIndex}
                    type="button"
                    variant="secondary"
                  >
                    {vPageIndex + 1}
                  </MotifFab>
                ))} */}

                <MotifCarousel title="Default Carousel">
                  {chunkedPages(selectedPages, 8)?.map((vChunk, vChunkIndex) => (
                    <span key={vChunkIndex} className="chunkContainer">
                      {vChunk.map((vPage, vPageIndex) => {
                        return(
                        <MotifTooltip
                          placement="bottom"
                          allowClick={false}
                          contentClassName="tooltipStyle"
                          trigger={
                            (isFileExcel(fileName) || !Number.isNaN(Number(vPage))) &&
                            <MotifFab
                              aria-label="Reference"
                              onClick={() =>
                                isFileExcel(fileName) ?
                                navigateToSheet(vPage) :
                                handlePageClick(vPage)
                              }
                              className={`referenceLink ${selectedPage === vPage ? "selected" : ""}`}
                              key={vPageIndex}
                              type="button"
                              variant="secondary"
                            >
                              {(selectedFileRefStartIndex + (vChunkIndex * 8) + vPageIndex)}
                            </MotifFab>
                          }
                        >
                         {isFileExcel(fileName) ? "Reference from excel" + vPage:"Reference from Page "+ vPage}
                        </MotifTooltip>
                      )})}
                    </span>
                  ))}
                </MotifCarousel>

              </div>
              <div ref={containerRef} className="source-document-content">
              {
                  document && !isFileExcel(fileName) &&
                    <Document
                      file={document}
                      onLoadSuccess={onDocumentLoadSuccess}
                      className="document-viewer"
                      loading={''}
                      onLoadError={handleOnLoadError}
                      noData={isDocLoaded ? msgNoData : msgLoading}
                    >
                      <Page
                        pageNumber={pageNumber}
                        canvasBackground="white"
                        scale={1}
                        loading={''}
                        customTextRenderer={textRenderer}
                        width={pageWidth}
                      />
                    </Document>
                }
                {
                  document && isFileExcel(fileName) &&
                  <XLViewer document={document} ref={xlViewerRef} />
                }
                  {
                    !isDocLoaded ?
                      <div>
                        <img
                          className="document-removed-rotate-icon"
                          src={loadingCircle}
                          alt="loading_icon"
                        />
                      </div>
                      : !document &&
                      <div className="no-docs-message">{msgNoDocs}</div>
                  }
              </div>

              <div className="source-document-footer">
                {/* <div className="paginatorWrapper">
                  <MotifIconButton
                    aria-label="Previous Page"
                    disabled={pageNumber <= 1}
                    onClick={previousPage}
                    className="iconPrevious"
                  >
                    <img src={nav_arrow_left} alt="Arrow Left" />
                  </MotifIconButton>
                  <div className="pageSelect">
                    {(pages.length > 0) &&
                      <MotifInput
                                    label="inputprojectname"
                                    onChange={(e)=>setCurrentPageNumber(e.target.value)}
                                    data-testid="inputprojectname"
                                    className='pageInput'
                                    value={currentPageNumber}
                                    hideClearButton={true}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                          goToPageNumber(e)
                                      }}
                                      width={30}
                                />
                    }
                  </div>
                  <div className="txtPageNo">/{numPages || "--"}</div>
                  <MotifIconButton
                    aria-label="Next Page"
                    disabled={pageNumber >= numPages}
                    onClick={nextPage}
                    className="iconNext"
                  >
                    <img src={nav_arrow_right} alt="Arrow Left" />
                  </MotifIconButton>
                </div> */}
             
              </div>
            </div>
          </div>
          </div>
        </MotifModalBody>
      </MotifModal>
    </div>
  );
};
export default ViewSourceDocsModal;
